import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Paper } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import { useToast } from './ui/ToastProvider';
import { transactionStore, CocCancellationRequest } from '../utils/transactionStore';
const CheckerCocCancellationTable: React.FC = () => {
  const [requests, setRequests] = useState<CocCancellationRequest[]>([]);
  const [selected, setSelected] = useState<CocCancellationRequest | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    showToast
  } = useToast();
  const refresh = () => setRequests(transactionStore.getPendingCocCancellations());
  useEffect(() => {
    refresh();
    const unsubscribe = transactionStore.subscribe(refresh);
    return unsubscribe;
  }, []);
  const handleAction = (row: CocCancellationRequest, action: 'approve' | 'reject') => {
    setSelected(row);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selected || !actionType) return;
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      if (actionType === 'approve') transactionStore.approveCocCancellation(selected.id, reason);else transactionStore.rejectCocCancellation(selected.id, reason);
      showToast(`COC cancellation ${actionType === 'approve' ? 'approved' : 'rejected'} for ${selected.transactionId}`, actionType === 'approve' ? 'success' : 'info');
      setActionDialogOpen(false);
      setReason('');
      setSelected(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const rowActions: RowAction<CocCancellationRequest>[] = [{
    icon: 'lucide:check-circle',
    label: 'Approve',
    onClick: row => handleAction(row, 'approve')
  }, {
    icon: 'lucide:x-circle',
    label: 'Reject',
    onClick: row => handleAction(row, 'reject')
  }];
  const columns: Column<CocCancellationRequest>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-572" data-mp="2r3i1r6nzzzzs">{row.id}</Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-573" data-mp="1yge2libl6fvx">{row.transactionId}</Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-574" data-mp="2855rfhlcok2m">{row.mtoName}</Typography>
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: () => <Chip label="OTC" size="small" sx={{
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      color: '#F59E0B',
      fontWeight: 700,
      fontSize: '0.7rem'
    }} data-id="element-575" data-mp="1aoqef85plixc" />
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-576" data-mp="1840wfuukfo81">{row.currencyFCY} {row.amountFCY}</Typography>
  }, {
    id: 'currentState',
    label: 'Current State',
    renderCell: row => <Chip label={row.currentState.replace(/_/g, ' ')} size="small" sx={{
      backgroundColor: 'rgba(100, 116, 139, 0.1)',
      color: '#64748B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-577" data-mp="y6moykqme9rv" />
  }, {
    id: 'makerReason',
    label: 'Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 220
    }} data-id="element-578" data-mp="p80ze5mqduhl">{row.resubmitReason || row.makerReason}</Typography>
  }, {
    id: 'requestedBy',
    label: 'Requested By',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-579" data-mp="fh6aeti3ogsg">{row.requestedBy}</Typography>
  }, {
    id: 'requestedAt',
    label: 'Requested At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-580" data-mp="5j0d6p78nytx">{new Date(row.resubmittedAt || row.requestedAt).toLocaleString()}</Typography>
  }];
  return <>
      <DataTable title="Pending COC Cancellation Requests" data={requests} columns={columns} rowActions={rowActions} keyField="id" emptyStateMessage="No pending COC cancellation requests" initialVisibleColumns={['transactionId', 'mtoName', 'payoutType', 'amountFCY', 'currentState', 'makerReason', 'requestedBy', 'requestedAt']} data-id="element-581" data-mp="q7ji7l0xod2l" />

      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-582" data-mp="7h1q9inxdlia">
        <DialogTitle data-id="element-583" data-mp="cgo49ubkv5s5">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-584" data-mp="cgxthfost8ws">
            <Icon icon={actionType === 'approve' ? 'lucide:check-circle' : 'lucide:x-circle'} width={24} color={actionType === 'approve' ? '#10B981' : '#EF4444'} data-id="element-585" data-mp="3um9fr4hx7ce7" />
            <Typography variant="h6" fontWeight={600} data-id="element-586" data-mp="2k1ldb2taosr">{actionType === 'approve' ? 'Approve' : 'Reject'} COC Cancellation</Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-587" data-mp="1wuolqystjmqo">
          <Box sx={{
          mt: 1
        }} data-id="element-588" data-mp="1wtvp4qnkqdm9">
            {selected && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-589" data-mp="3hek1sno3p7te">
                  Transaction ID: <strong data-id="element-590" data-mp="3de051l7fw67s">{selected.transactionId}</strong>
                </Typography>
                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-591" data-mp="3r4lr1is6ww9w">
                  <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-592" data-mp="3mcv9osuu588z">Maker's Reason</Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-593" data-mp="4vaptftfs218">{selected.makerReason}</Typography>
                </Paper>
                {selected.resubmitReason && <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: 'rgba(245, 158, 11, 0.05)',
              borderColor: '#FCD34D'
            }} data-id="element-594" data-mp="dx1uchc8q2ti">
                    <Typography variant="caption" color="#D97706" fontWeight={600} data-id="element-595" data-mp="8za6byepv0ul">Previous Rejection Reason</Typography>
                    <Typography variant="body2" sx={{
                mt: 0.5,
                mb: 1.5
              }} data-id="element-596" data-mp="2fp6siakc1wco">{selected.checkerComment}</Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-597" data-mp="2arojkk6bfh4o">Maker's Resubmit Reason</Typography>
                    <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-598" data-mp="jlintriipfuy">{selected.resubmitReason}</Typography>
                  </Paper>}
              </>}
            <TextField label={`Reason for ${actionType === 'approve' ? 'Approval' : 'Rejection'}`} placeholder={`Enter reason for ${actionType === 'approve' ? 'approving' : 'rejecting'} this cancellation...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-599" data-mp="ojbtmjfws7up" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-600" data-mp="17c5wif09c9ik">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-601" data-mp="17dh0ofxm7ja7">Cancel</Button>
          <Button variant="contained" color={actionType === 'approve' ? 'success' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-603" data-mp="1lbf974p98lhs" /> : <Icon icon={actionType === 'approve' ? 'lucide:check' : 'lucide:x'} width={16} data-id="element-604" data-mp="35sokt2sy076i" />} data-id="element-602" data-mp="1cc0neik18z3m">
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>;
};
export default CheckerCocCancellationTable;